// useNarrow — viewport-width responsive hook. Returns true when
// `window.innerWidth` is below `threshold` px. Updates on resize.
// SSR-safe: assumes non-narrow when `window` is undefined.
import { useEffect, useState } from 'react';

/**
 * @aiPurpose React hook that returns true when the viewport is narrower than the
 *   given threshold (default 900px). Used by DataTable and feature surfaces to
 *   switch between desktop tables and mobile cards.
 * @aiInvariant Re-render is driven by a window resize listener; the initial
 *   value reads `window.innerWidth` synchronously so the first paint is correct
 *   on the client. In SSR the hook returns false.
 */
export function useNarrow(threshold = 900): boolean {
  const [narrow, setNarrow] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < threshold : false,
  );
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < threshold);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [threshold]);
  return narrow;
}
